const notCalled = Symbol('not called');

// eslint-disable-next-line max-len
export function once<Params extends Array<unknown>, Return>(fn: (...args: Params) => Return): typeof fn {
    let value: Return | typeof notCalled = notCalled;

    return function wrappedFn(...args: Params) {
        if (value === notCalled) {
            value = fn(...args);
        }

        return value;
    };
}

export function noop() {}

// eslint-disable-next-line @typescript-eslint/ban-types
export function isCallback<T = Function>(maybeFunc: T | unknown): maybeFunc is T {
    return typeof maybeFunc === 'function';
}

export interface Callable {
    (...args: any[]): any;
}

export function debounce<F extends Callable>(fn: F, timeout: number = 300) {
    let timer: ReturnType<typeof setTimeout>;

    let wrapper: (...args: Parameters<F>) => void = (...args: Parameters<F>) => {
        clearTimeout(timer);

        timer = setTimeout(() => {
            fn.apply(null, args);
        }, timeout);
    };

    return wrapper;
}

export function debounceAsync<F extends (...args: any[]) => Promise<any>>(fn: F, timeout: number = 300) {
    let timersMap: Map<string, ReturnType<typeof setTimeout>> = new Map();
    let promiseMap: Map<string, Promise<Awaited<ReturnType<F>>>> = new Map();
    let resolveMap: Map<string, (value: Awaited<ReturnType<F>>) => void> = new Map();

    let wrapper: (...args: Parameters<F>) => Promise<Awaited<ReturnType<F>>> = (...args: Parameters<F>) => {
        const key = JSON.stringify(args);

        const promise = promiseMap.has(key)
            ? promiseMap.get(key)!
            : new Promise<Awaited<ReturnType<F>>>((resolve) => {
                  resolveMap.set(key, resolve);
              });

        clearTimeout(timersMap.get(key));
        timersMap.delete(key);

        const timer = setTimeout(() => {
            fn.apply(null, args).then((retval) => {
                resolveMap.get(key)?.(retval);

                promiseMap.delete(key);
                resolveMap.delete(key);
            });
        }, timeout);

        timersMap.set(key, timer);

        return promise;
    };

    return wrapper;
}

export function throttle<F extends Callable>(fn: F, timeout: number = 300) {
    let locked: boolean;
    let wrapper: (...args: Parameters<F>) => void = (...args: Parameters<F>) => {
        if (locked) return;
        locked = true;
        setTimeout(() => {
            fn(...args);
            locked = false;
        }, timeout);
    };
    return wrapper;
}

export function memoize<F extends Callable, Key>(
    fn: F,
    getKey: (...args: Parameters<F>) => Key = (...args) => args.toString() as Key,
    { resetCacheOnMiss }: { resetCacheOnMiss: boolean } = {
        resetCacheOnMiss: false,
    },
) {
    let cache: Map<Key, any> = new Map();

    return (...args: Parameters<F>): ReturnType<F> => {
        const cacheKey = getKey(...args);

        if (cache.get(cacheKey) !== undefined) return cache.get(cacheKey);

        if (resetCacheOnMiss) {
            cache.clear();
        }

        const result = fn(...args);
        cache.set(cacheKey, result);

        return result;
    };
}

export function pipe<T extends Callable, U extends Callable, R extends Callable>(...[fn1, ...fns]: [T, ...U[], R]): (...args: Parameters<T>) => ReturnType<R> {
    return fns.reduce((prevFn, nextFn) => (value) => nextFn(prevFn(value)), fn1 as Callable);
}

export const identity = <T>(something: T): T => something;

export const castCallable = <Something extends Callable | any>(something: Something): Something extends Callable ? Something : () => Something => {
    if (isCallback(something)) {
        return something as Something extends Callable ? Something : () => Something;
    }
    return (() => something) as Something extends Callable ? Something : () => Something;
};
